import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../../../styles/components/dialogs/agents/selectCustomerDialogComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import SelectInputElementComponent from "../../general/elements/inputElements/SelectInputElementComponent";
import agentOrderActions from "../../../store/actions/agentOrder/agentOrder.actions";
import useCustomersForAgentQuery from "../../../hooks/queries/useCustomersForAgentQuery";
import useDelayedFlag from "../../../hooks/useDelayedFlag";
import { SkeletonSelect } from "../../general/skeleton";

// 0011: shared business-customer label format, consistent with the create-account dialog and the
// (now-removed) customer-picker page.
const customerLabel = (customer) => {
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return name ? `${name} - ${customer.customerId}` : `${customer.customerId}`;
};

// 0023: the agent/admin picks the business customer they will order on behalf of. This dialog
// REPLACES the old full-page customer picker — same flow, no parallel state:
//   fetch via the shared 0016 RQ hook → pick one → dispatch the SAME 0017 setSelectedCustomer
//   (so the bar + cart banner + order customerId all reflect it) → navigate("/items").
// It renders under .dialogContainer so it inherits the 0020 gutter/clamp + the 0021 bar-offset,
// and uses the searchableList inline picker so the options scroll WITHIN the dialog and never clip
// against the bar (spec 0023 §R5).
const SelectCustomerDialogComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedCustomer = useSelector((state) => state.agentOrderReducer.selectedCustomer);

  // 0016: the agent-scoped customer list comes from the shared React Query hook (one deduped
  // fetch, cached across consumers). No new fetch path is introduced here.
  const { data: customers = [], isLoading, isError } = useCustomersForAgentQuery();

  // 0018: only show the skeleton after ~120ms so a cached (5-min staleTime) re-open shows the
  // picker instantly with no skeleton flash.
  const showSkeleton = useDelayedFlag(isLoading);

  useEffect(() => {
    if (isError) {
      AlertService.error("טעינת רשימת הלקוחות נכשלה", "אנא נסה שנית מאוחר יותר");
    }
  }, [isError]);

  const handleSelectCustomer = (customer) => {
    // Same Redux selection the bar/cart/order read — no parallel state (spec 0023 §R4).
    dispatch(agentOrderActions.setSelectedCustomer(customer));
    AlertService.success("הלקוח נבחר", `כעת ניתן לבנות הזמנה עבור ${customer.firstName} ${customer.lastName}`);
    // D2: leave to the catalog. navigate("/items") drops the ?d/action params, closing the dialog.
    navigate("/items");
  };

  // searchableList onChange contract: receives the picked option's VALUE (the customerId). Map it
  // back to the full customer object — exactly as the old page's handleCustomerChange did.
  const handleCustomerChange = (selectedValue) => {
    const customer = customers.find((c) => String(c.customerId) === String(selectedValue));
    if (customer) handleSelectCustomer(customer);
  };

  const getCustomerSelectElement = () => {
    const inputProps = {
      name: "selectedCustomerId",
      label: "לקוח",
      value: selectedCustomer?.customerId || "",
      options: customers.map((customer) => ({ value: customer.customerId, label: customerLabel(customer) })),
      onChange: handleCustomerChange,
      searchableList: true,
    };
    return <SelectInputElementComponent props={inputProps} />;
  };

  // Two-region layout (pinned header + scrollable body) so the title stays put while the picker
  // scrolls. The clamp subtracts --dialog-bar-offset so it stays clipping-safe with the bar.
  return (
    <div className="dialogContainer selectCustomerDialogContainer">
      <div className="selectCustomerDialogHeader">
        <h1>בחירת לקוח להזמנה</h1>
        <p>בחר את הלקוח שעבורו תבוצע ההזמנה. לאחר הבחירה ניתן לבנות את הסל ולבצע את ההזמנה כרגיל.</p>
        {selectedCustomer && (
          <p className="selectCustomerDialogCurrent">
            לקוח נבחר כעת: <b>{selectedCustomer.firstName} {selectedCustomer.lastName}</b> (קוד {selectedCustomer.customerId})
          </p>
        )}
      </div>

      <div className="selectCustomerDialogBody">{isLoading ? showSkeleton && <SkeletonSelect /> : getCustomerSelectElement()}</div>
    </div>
  );
};

export default SelectCustomerDialogComponent;
